import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VolumeDownIcon from '@mui/icons-material/VolumeDown';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {
  Chip,
  IconButton,
  Slider,
  Stack,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

const formWaveSurferOptions = ref => ({
  container: ref,
  waveColor: '#eee',
  progressColor: 'OrangeRed',
  cursorColor: 'OrangeRed',
  barWidth: 3,
  barRadius: 3,
  responsive: true,
  height: 150,
  // If true, normalize by the maximum peak instead of 1.0.
  normalize: true,
  // Use the PeakCache to improve rendering speed of large waveforms.
  partialRender: true,
});

export default function Waveform({ url }) {
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const [playtime, setPlaytime] = useState(0);
  const [duration, setDuration] = useState(0);

  // create new WaveSurfer instance
  // On component mount and when url changes
  useEffect(() => {
    setIsPlaying(false);

    const options = formWaveSurferOptions(waveformRef.current);
    wavesurfer.current = WaveSurfer.create(options);

    wavesurfer.current.load(url);

    wavesurfer.current.on('ready', () => {
      if (wavesurfer.current) {
        wavesurfer.current.setVolume(volume);
        setVolume(volume);
        setPlaytime(0);
        setDuration(wavesurfer.current.getDuration());
      }
    });

    wavesurfer.current.on('finish', () => {
      if (wavesurfer.current) {
        setIsPlaying(false);
        setPlaytime(0);
      }
    });
    wavesurfer.current.on('audioprocess', () => {
      if (wavesurfer.current) {
        setPlaytime(wavesurfer.current.getCurrentTime());
      }
    });
    wavesurfer.current.on('seek', e => {
      if (wavesurfer.current) {
        setPlaytime(e * wavesurfer.current.getDuration());
      }
    });
    // Removes events, elements and disconnects Web Audio nodes when component unmount
    return () => wavesurfer.current.destroy();
  }, [url]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
    wavesurfer.current.playPause();
  };

  const handleVolumeChange = e => {
    const { target } = e;
    const newVolume = +target.value;

    if (newVolume) {
      setVolume(newVolume);
      wavesurfer.current.setVolume(newVolume || 1);
    }
  };

  const formatTime = (secondsToFormat) => {
    const minutes = Math.floor(parseInt(secondsToFormat) / 60);
    let seconds = parseInt(secondsToFormat % 60);
    seconds = (seconds < 10) ? '0' + seconds : '' + seconds;
    return `${minutes}:${seconds}`;
  };

  return (
    <div style={{ width: '100%' }}>
      <div ref={waveformRef} />
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        spacing={2}
      >
        <Stack direction='row'>
          <IconButton onClick={handlePlayPause} size='large'>
            {isPlaying ? <PauseCircleOutlineIcon fontSize='large' /> : <PlayCircleOutlineIcon fontSize='large' />}
          </IconButton>
          <Stack spacing={2} direction='row' sx={{ mb: 1 }} alignItems='center'>
            <VolumeDownIcon />
            <Slider
              value={volume}
              onChange={handleVolumeChange}
              step={0.1}
              min={0.01}
              max={1}
              sx={{ minWidth: 150 }}
            />
            <VolumeUpIcon />
          </Stack>
        </Stack>
        <Chip label={`${formatTime(playtime)} / ${formatTime(duration)}`} />
      </Stack>
    </div>
  );
}
